import { PerformanceMetric, usePerformance } from '@/context/performance';
import { useBackgroundAppBundle } from '@/context/background-app-bundle';
import { activeRouteStore, setChildAppLoaded } from '@customerio/hydra-nav-state';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppBundle } from '../../utils/app-bundles';
import { PageLoader } from '@customerio/pluma-components/react';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { sentryAddAppLoadBreadcrumb, sentryCaptureAppLoadDiagnostic } from '@/utils/sentry';

const APP_BUNDLE_LOAD_DIAGNOSTIC_TIMEOUT_MS = 30_000;

interface AppBundleLoaderProps {
  appBundle: AppBundle;
  className?: string;
  renderApp: (appWrapper: HTMLDivElement) => Promise<() => void>;
  style?: string;
}

function getPathWithoutSearch(path: string) {
  return path.split('?')[0] ?? path;
}

function getAppWrapperDiagnosticData(appWrapper: HTMLDivElement | undefined) {
  const textContent = appWrapper?.textContent?.trim() ?? '';

  return {
    appWrapperPresent: appWrapper != null,
    appWrapperDisplay: appWrapper?.style.display ?? null,
    appWrapperChildElementCount: appWrapper?.childElementCount ?? null,
    appWrapperTextLength: textContent.length,
    appWrapperContainsLoadingText: textContent.toLowerCase().includes('loading'),
  };
}

function getDiagnosticTimeoutDelay(loadStartedAt: number | undefined) {
  const elapsedMs = loadStartedAt != null ? performance.now() - loadStartedAt : 0;
  return Math.max(0, APP_BUNDLE_LOAD_DIAGNOSTIC_TIMEOUT_MS - elapsedMs);
}

type DiagnosticTimeout = {
  loadPromise: Promise<void>;
  timeoutId: number;
};

/** Loads an app bundle, and keeps it mounted in the background while the user is viewing another app,
 * to improve performance when switching between different apps.
 *
 * This is done to provide a seamless experience for the user, as the borders between the different apps
 * are invisible from the user's perspective.
 */
export function AppBundleLoader({ appBundle, className = '', renderApp, style }: AppBundleLoaderProps) {
  const ref = useRef<HTMLDivElement>(null);
  const diagnosticTimeoutRef = useRef<DiagnosticTimeout | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const recordPerformanceMetric = usePerformance();
  const { getData: getBackgroundAppData, setData: setBackgroundAppData } = useBackgroundAppBundle(appBundle);
  const [loaded, setIsLoaded] = useState(() => getBackgroundAppData()?.loaded === true);

  if (loadError != null) {
    throw loadError;
  }

  useMountEffect(() => {
    if (getBackgroundAppData()?.loaded === true) {
      return;
    }

    setChildAppLoaded(false);
  });

  const attachAppWrapper = useCallback(() => {
    const appData = getBackgroundAppData();
    const appWrapper = appData?.appWrapper;
    const parentElement = ref.current?.parentElement;

    if (appWrapper != null && parentElement != null && appWrapper.parentElement !== parentElement) {
      parentElement.appendChild(appWrapper);
    }

    return appWrapper;
  }, [getBackgroundAppData]);

  const hideAppWrapper = useCallback(() => {
    const appWrapper = getBackgroundAppData()?.appWrapper;
    if (appWrapper != null) {
      appWrapper.style.display = 'none';
    }
  }, [getBackgroundAppData]);

  const showAppWrapper = useCallback(() => {
    const appWrapper = attachAppWrapper();
    if (appWrapper != null) {
      appWrapper.style.display = 'flex';
    }
  }, [attachAppWrapper]);

  const clearDiagnosticTimeout = useCallback((loadPromise?: Promise<void>) => {
    const diagnosticTimeout = diagnosticTimeoutRef.current;
    if (diagnosticTimeout == null) {
      return;
    }
    if (loadPromise != null && diagnosticTimeout.loadPromise !== loadPromise) {
      return;
    }

    window.clearTimeout(diagnosticTimeout.timeoutId);
    diagnosticTimeoutRef.current = null;
  }, []);

  const armDiagnosticTimeout = useCallback(
    (loadPromise: Promise<void>) => {
      if (diagnosticTimeoutRef.current?.loadPromise === loadPromise) {
        return;
      }

      clearDiagnosticTimeout();

      const diagnosticLoadStartedAt = getBackgroundAppData().loadStartedAt;
      const timeoutId = window.setTimeout(() => {
        const activeDiagnosticTimeout = diagnosticTimeoutRef.current;
        if (activeDiagnosticTimeout?.loadPromise !== loadPromise) {
          return;
        }

        const timedOutAppData = getBackgroundAppData();
        if (timedOutAppData?.loaded === true) {
          return;
        }

        sentryCaptureAppLoadDiagnostic('background_app_load_timeout', {
          appBundle,
          elapsedMs:
            diagnosticLoadStartedAt != null
              ? Math.round(performance.now() - diagnosticLoadStartedAt)
              : APP_BUNDLE_LOAD_DIAGNOSTIC_TIMEOUT_MS,
          activeRoutePath: getPathWithoutSearch(activeRouteStore.get()),
          initialized: timedOutAppData?.initialized === true,
          loadPromisePresent: timedOutAppData?.loadPromise != null,
          ...getAppWrapperDiagnosticData(timedOutAppData?.appWrapper),
        });
      }, getDiagnosticTimeoutDelay(diagnosticLoadStartedAt));

      diagnosticTimeoutRef.current = { loadPromise, timeoutId };
    },
    [appBundle, clearDiagnosticTimeout, getBackgroundAppData],
  );

  const load = useCallback(async () => {
    const loadStartedAt = performance.now();
    recordPerformanceMetric(PerformanceMetric.ChildAppLoadStart);
    sentryAddAppLoadBreadcrumb('background app load started', { appBundle });

    const appWrapperTemplate = document.createElement('template');
    appWrapperTemplate.innerHTML = `<div class="${className}" style="display: none; min-width: 0; flex: 1; ${
      style ?? ''
    }"></div>`;

    const appWrapper = appWrapperTemplate.content.cloneNode(true).firstChild as HTMLDivElement;

    setBackgroundAppData({
      initialized: true,
      loaded: false,
      appWrapper,
      loadStartedAt,
    });

    attachAppWrapper();

    const cleanup = await renderApp(appWrapper);
    sentryAddAppLoadBreadcrumb('background app render resolved', { appBundle });

    setBackgroundAppData({
      loaded: true,
      cleanup,
    });
  }, [appBundle, attachAppWrapper, className, renderApp, recordPerformanceMetric, setBackgroundAppData, style]);

  /** When this component is rendered for the first time, it loads the app bundle, if it hasn't been loaded yet, and displays it.
   * When this component is unmounted, it hides the app wrapper (and the app itself), instead of destroying it.
   * The next times this component is rendered, it will display the previous app wrapper again, instead of re-loading and re-rendering it.
   * The effect below controls this behavior.
   */
  useEffect(() => {
    const appData = getBackgroundAppData();

    if (appData?.initialized !== true) {
      setBackgroundAppData({
        loadPromise: load(),
      });
    } else if (appData.loaded === true) {
      setChildAppLoaded(true);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
      setIsLoaded(true);
      showAppWrapper();
    }

    const loadPromise = getBackgroundAppData().loadPromise;
    let cancelLoadListener = false;
    if (loadPromise != null && !loaded) {
      armDiagnosticTimeout(loadPromise);

      loadPromise
        .then(() => {
          clearDiagnosticTimeout(loadPromise);

          // If the user switches to a different app bundle while this app is loaded,
          if (cancelLoadListener) {
            sentryAddAppLoadBreadcrumb('background app load listener cancelled before resolve', { appBundle });
            return;
          }

          sentryAddAppLoadBreadcrumb('background app load resolved', { appBundle });
          setChildAppLoaded(true);
          setIsLoaded(true);
          showAppWrapper();
        })
        .catch((error: unknown) => {
          clearDiagnosticTimeout(loadPromise);

          if (cancelLoadListener) {
            sentryAddAppLoadBreadcrumb(
              'background app load listener cancelled before rejection',
              { appBundle },
              'error',
            );
            return;
          }

          sentryCaptureAppLoadDiagnostic(
            'background_app_load_rejected',
            {
              appBundle,
              activeRoutePath: getPathWithoutSearch(activeRouteStore.get()),
              errorName: error instanceof Error ? error.name : typeof error,
              errorMessage: error instanceof Error ? error.message : String(error),
              ...getAppWrapperDiagnosticData(getBackgroundAppData()?.appWrapper),
            },
            { level: 'error' },
          );
          setLoadError(error instanceof Error ? error : new Error(`Failed to load ${appBundle}.`));
        });
    } else if (loaded) {
      sentryAddAppLoadBreadcrumb('background app wrapper shown from loaded state', { appBundle });
      showAppWrapper();
    }

    return () => {
      sentryAddAppLoadBreadcrumb('background app wrapper hidden', { appBundle });
      hideAppWrapper();
      cancelLoadListener = true;
    };
  }, [
    appBundle,
    className,
    renderApp,
    armDiagnosticTimeout,
    clearDiagnosticTimeout,
    getBackgroundAppData,
    load,
    loaded,
    setChildAppLoaded,
    setIsLoaded,
    showAppWrapper,
    hideAppWrapper,
  ]);

  useEffect(() => {
    return () => {
      clearDiagnosticTimeout();
    };
  }, [appBundle, clearDiagnosticTimeout]);

  return (
    <>
      <div ref={ref} style={{ display: 'none' }} />
      {loaded ? null : <PageLoader />}
    </>
  );
}
